'use client';

import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import Image from "next/image";
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { montserratMedium } from "../fonts";
import HomeSectionTitle from './home-section-title';
import AppButton from './app-button';
import GloryBorder from './glory-border';

// Throttle utility for scroll events
function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export default function TradingPlatformDesktop() {
  const t = useTranslations('home.tradingPlatform');
  const tradingPlatformRef = useRef<HTMLElement>(null);
  const [isTradingPlatformVisible, setIsTradingPlatformVisible] = useState(false);
  const [tradingPlatformScrollProgress, setTradingPlatformScrollProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastProgressRef = useRef<number>(0);
  
  // Use spring animation for smooth deceleration - Image scale
  const scaleMotionValue = useMotionValue(1.0);
  const scaleSpring = useSpring(scaleMotionValue, {
    stiffness: 100,
    damping: 30,
    mass: 1,
  });

  // Spring animations for cards - Card 1
  const card1Opacity = useMotionValue(0);
  const card1TranslateX = useMotionValue(300);
  const card1OpacitySpring = useSpring(card1Opacity, { stiffness: 100, damping: 30, mass: 1 });
  const card1TranslateXSpring = useSpring(card1TranslateX, { stiffness: 100, damping: 30, mass: 1 });

  // Spring animations for cards - Card 2
  const card2Opacity = useMotionValue(0);
  const card2TranslateX = useMotionValue(300);
  const card2OpacitySpring = useSpring(card2Opacity, { stiffness: 100, damping: 30, mass: 1 });
  const card2TranslateXSpring = useSpring(card2TranslateX, { stiffness: 100, damping: 30, mass: 1 });

  // Spring animations for cards - Card 3
  const card3Opacity = useMotionValue(0);
  const card3TranslateX = useMotionValue(-300);
  const card3OpacitySpring = useSpring(card3Opacity, { stiffness: 100, damping: 30, mass: 1 });
  const card3TranslateXSpring = useSpring(card3TranslateX, { stiffness: 100, damping: 30, mass: 1 });

  // Spring animations for cards - Card 4
  const card4Opacity = useMotionValue(0);
  const card4TranslateX = useMotionValue(-300);
  const card4OpacitySpring = useSpring(card4Opacity, { stiffness: 100, damping: 30, mass: 1 });
  const card4TranslateXSpring = useSpring(card4TranslateX, { stiffness: 100, damping: 30, mass: 1 });
  
  const handleLaunchApp = () => {
    window.open('https://app.xox.exchange/', '_self');
  };

  // Scroll progress handler for trading platform section
  const calculateProgress = useCallback(() => {
    if (!tradingPlatformRef.current) return;
    
    const section = tradingPlatformRef.current;
    // Batch all geometry reads together to avoid forced reflow
    const rect = section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionHeight = section.offsetHeight;
    const maxScroll = sectionHeight - windowHeight;
    
    if (maxScroll <= 0) {
      if (lastProgressRef.current !== 1) {
        setTradingPlatformScrollProgress(1);
        lastProgressRef.current = 1;
      }
      return;
    }
    
    let progress = 0;
    
    if (rect.top > 0) {
      progress = 0;
    } else if (rect.bottom < windowHeight) {
      progress = 1;
    } else {
      const scrolledPast = -rect.top;
      progress = scrolledPast / maxScroll;
    }
    
    progress = Math.max(0, Math.min(1, progress));
    
    // Quantize to reduce re-renders
    const quantized = Math.round(progress * 100) / 100;
    if (Math.abs(quantized - lastProgressRef.current) > 0.005) {
      setTradingPlatformScrollProgress(quantized);
      lastProgressRef.current = quantized;
    }
  }, []);

  // Throttled scroll handler using requestAnimationFrame
  const handleScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      calculateProgress();
    });
  }, [calculateProgress]);

  // Throttled version for scroll events (60fps = ~16ms)
  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, 16),
    [handleScroll]
  );

  useEffect(() => {
    const tradingPlatformObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTradingPlatformVisible(true);
        }
      },
      { threshold: 0, rootMargin: '200px 0px' }
    );

    if (tradingPlatformRef.current) {
      tradingPlatformObserver.observe(tradingPlatformRef.current);
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    calculateProgress(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (tradingPlatformRef.current) {
        tradingPlatformObserver.unobserve(tradingPlatformRef.current);
      }
    };
  }, [throttledHandleScroll, calculateProgress]);

  // Memoize style calculations to prevent recalculation on every render
  const backgroundImageStyle = useMemo(() => {
    const isFixed = tradingPlatformScrollProgress > 0 && tradingPlatformScrollProgress < 1;
    return {
      position: isFixed ? 'fixed' as const : 'absolute' as const,
      willChange: isFixed ? 'position' : 'auto',
      ...(isFixed
        ? { bottom: 0, right: "-20%" }
        : tradingPlatformScrollProgress <= 0
        ? { top: 0, left: 0, right: 0, bottom: 0 }
        : { bottom: 0, right: "-20%" }),
    };
  }, [tradingPlatformScrollProgress]);

  const contentContainerStyle = useMemo(() => {
    const isFixed = tradingPlatformScrollProgress > 0 && tradingPlatformScrollProgress < 1;
    return {
      position: isFixed ? 'fixed' as const : 'absolute' as const,
      willChange: isFixed ? 'position' : 'auto',
      ...(isFixed
        ? { top: 0, left: 0, right: 0, bottom: 0, width: '100%' }
        : tradingPlatformScrollProgress <= 0
        ? { top: 0, left: 0, right: 0, width: '100%' }
        : { bottom: 0, left: 0, right: 0, width: '100%' }),
    };
  }, [tradingPlatformScrollProgress]);

  const titleStyle = useMemo(() => {
    let opacity = 0;
    let translateY = 50;
    
    if (tradingPlatformScrollProgress >= 0.2 && tradingPlatformScrollProgress <= 0.3) {
      const progress = (tradingPlatformScrollProgress - 0.2) / (0.3 - 0.2);
      opacity = progress;
      translateY = 50 - (progress * 50);
    } else if (tradingPlatformScrollProgress > 0.3) {
      opacity = 1;
      translateY = 0;
    }
    
    return {
      opacity,
      transform: `translate3d(0, ${translateY}px, 0)`,
      willChange: 'opacity, transform',
    };
  }, [tradingPlatformScrollProgress]);

  const buttonStyle = useMemo(() => {
    let opacity = 0;
    if (tradingPlatformScrollProgress >= 0.9 && tradingPlatformScrollProgress <= 1.0) {
      opacity = (tradingPlatformScrollProgress - 0.9) / 0.1;
    } else if (tradingPlatformScrollProgress > 1.0) {
      opacity = 1;
    }
    
    return {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      width: '156px',
      height: '50px',
      zIndex: 10,
      transform: 'translate3d(-50%, -50%, 0)',
      opacity,
      willChange: 'opacity, transform',
    };
  }, [tradingPlatformScrollProgress]);


  // Helper function to calculate card animation values
  const calculateCardValues = useCallback((progress: number, start: number, end: number, initialX: number) => {
    if (progress >= start && progress <= end) {
      const localProgress = (progress - start) / (end - start);
      return {
        opacity: localProgress,
        translateX: initialX - (localProgress * initialX),
      };
    } else if (progress > end) {
      return { opacity: 1, translateX: 0 };
    }
    return { opacity: 0, translateX: initialX };
  }, []);

  // Calculate target values from scroll progress and update springs
  useEffect(() => {
    const progress = tradingPlatformScrollProgress;
    
    // Image scale
    const targetScale = progress <= 0.1 ? 1.4 - (progress / 0.1) * 0.4 : 1.0;
    scaleMotionValue.set(targetScale);

    // Cards - calculate and update
    const card1 = calculateCardValues(progress, 0.35, 0.45, 300);
    card1Opacity.set(card1.opacity);
    card1TranslateX.set(card1.translateX);

    const card2 = calculateCardValues(progress, 0.55, 0.65, 300);
    card2Opacity.set(card2.opacity);
    card2TranslateX.set(card2.translateX);

    const card3 = calculateCardValues(progress, 0.35, 0.45, -300);
    card3Opacity.set(card3.opacity);
    card3TranslateX.set(card3.translateX);

    const card4 = calculateCardValues(progress, 0.55, 0.65, -300);
    card4Opacity.set(card4.opacity);
    card4TranslateX.set(card4.translateX);
  }, [
    tradingPlatformScrollProgress,
    scaleMotionValue,
    card1Opacity,
    card1TranslateX,
    card2Opacity,
    card2TranslateX,
    card3Opacity,
    card3TranslateX,
    card4Opacity,
    card4TranslateX,
    calculateCardValues,
  ]);

  // Transform spring values to CSS transform strings
  const scaleTransform = useTransform(scaleSpring, (scale) => 
    `translate3d(0, 0, 0) scale3d(${scale}, ${scale}, 1)`
  );

  const card1Transform = useTransform(card1TranslateXSpring, (x) => `translate3d(${x}px, 0, 0)`);
  const card2Transform = useTransform(card2TranslateXSpring, (x) => `translate3d(${x}px, 0, 0)`);
  const card3Transform = useTransform(card3TranslateXSpring, (x) => `translate3d(${x}px, 0, 0)`);
  const card4Transform = useTransform(card4TranslateXSpring, (x) => `translate3d(${x}px, 0, 0)`);

  return (
    <section
      ref={tradingPlatformRef}
      id="trading-platform"
      className="relative"
      style={{ height: '300vh', borderTop: '1px solid #030303', borderRadius: "25px" }}
    >
      {/* Desktop Version */}
      <div
        className="h-screen flex items-center justify-center overflow-hidden"
        style={contentContainerStyle}
      >
        <div className="w-full max-w-7xl mx-auto px-6">
          <div style={titleStyle}>
            <HomeSectionTitle
              subtitle={t('subtitle')}
              title={t('title')}
              centered
            />
          </div>
          <div className={`w-full flex gap-4 ${
            isTradingPlatformVisible 
              ? 'opacity-100 scale-100' 
              : 'opacity-0 scale-75'
          }`}>
            <div className="w-full max-w-[200px] gap-[32px] flex-col flex flex-1 items-center justify-center">
              <AppButton
                text={t('startTrading')}
                iconHover="/icons/mobile.svg"
                variant="outline"
                size="sm"
                style={buttonStyle}
                onClick={handleLaunchApp}
              />
              {/* Card 1: 50% to 70% - opacity 0-1, x-axis 300 to 0 */}
              <motion.div
                style={{
                  opacity: card1OpacitySpring,
                  transform: card1Transform,
                  willChange: 'opacity, transform',
                }}
              >
                <div className="w-[180px] h-[120px] xl:w-[200px] xl:h-[140px] flex flex-col items-center justify-center gap-2 border-1 border-[#111111] bg-[#111111] rounded-2xl p-4">
                  <Image
                    src="/icons/take-icon-1.svg"
                    alt={t('features.ultraLowCosts')}
                    width={40}
                    height={40}
                    className="opacity-100"
                  />
                  <p className={`${montserratMedium.className} text-center text-sm text-[color:var(--text-primary)]`}>{t('features.ultraLowCosts')}</p>
                </div>
              </motion.div>
              {/* Card 2: 70% to 90% - opacity 0-1, x-axis -300 to 0 */}
              <motion.div
                style={{
                  opacity: card2OpacitySpring,
                  transform: card2Transform,
                  willChange: 'opacity, transform',
                }}
              >
                <div className="w-[180px] h-[120px] xl:w-[200px] xl:h-[140px] flex flex-col items-center justify-center gap-2 border-1 border-[#111111] bg-[#111111] rounded-2xl p-4">
                  <Image
                    src="/icons/take-icon-2.svg"
                    alt={t('features.nearZeroSlippage')}
                    width={40}
                    height={40}
                    className="opacity-100"
                  />
                  <p className={`${montserratMedium.className} text-center text-sm text-[color:var(--text-primary)]`}>{t('features.nearZeroSlippage')}</p>
                </div>
              </motion.div>
            </div>
            <div className="w-full h-full flex-3 items-center justify-center z-2">
              <motion.div
                style={{
                  transform: scaleTransform,
                  transformOrigin: 'center center',
                  willChange: 'transform',
                }}
              >
                <GloryBorder>
                          <Image
                            src="/assets/trading-platform.webp"
                            alt={t('altText')}
                            width={3504}
                            height={1916}
                            className="w-full h-auto rounded-[22px]"
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 90vw, (max-width: 1280px) 80vw, 1200px"
                            priority
                            quality={75}
                          />
                </GloryBorder>
              </motion.div>
            </div>
            <div className="w-full max-w-[200px] gap-[32px] flex flex-col flex-1 items-center justify-center">
              {/* Card 3: 50% to 70% - opacity 0-1, x-axis 300 to 0 */}
              <motion.div
                style={{
                  opacity: card3OpacitySpring,
                  transform: card3Transform,
                  willChange: 'opacity, transform',
                }}
              >
                <div className="w-[180px] h-[120px] xl:w-[200px] xl:h-[140px] flex flex-col items-center justify-center gap-2 border-1 border-[#111111] bg-[#111111] rounded-2xl p-4">
                  <Image
                    src="/icons/take-icon-3.svg"
                    alt={t('features.broadAssetCoverage')}
                    width={40}
                    height={40}
                    className="opacity-100"
                  />
                  <p className={`${montserratMedium.className} text-center text-sm text-[color:var(--text-primary)]`}>{t('features.broadAssetCoverage')}</p>
                </div>
              </motion.div>
              {/* Card 4: 70% to 90% - opacity 0-1, x-axis -300 to 0 */}
              <motion.div
                style={{
                  opacity: card4OpacitySpring,
                  transform: card4Transform,
                  willChange: 'opacity, transform',
                }}
              >
                <div className="w-[180px] h-[120px] xl:w-[200px] xl:h-[140px] flex flex-col items-center justify-center gap-2 border-1 border-[#111111] bg-[#111111] rounded-2xl p-4">
                  <Image
                    src="/icons/take-icon-4.svg"
                    alt={t('features.instantExecution')}
                    width={40}
                    height={40}
                    className="opacity-100"
                  />
                  <p className={`${montserratMedium.className} text-center text-sm text-[color:var(--text-primary)]`}>{t('features.instantExecution')}</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

